class PixelatedEffect {
  constructor(options = {}) {
    this.config = {
      selector: '.pixelated-effect__wrapper',
      loadedClass: 'loaded',
      errorClass: 'load-failed',
      delay: 300,
      enableLogging: false,
      minDisplayTime: 200, // Minimum time to show pixelated image
      ...options
    };

    this.elements = new Set();
    this.elementTimestamps = new Map(); // Track when processing started for each element

    this.handleImageLoad = this.handleImageLoad.bind(this);
    this.handleImageError = this.handleImageError.bind(this);

    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.scanAndApply());
    } else {
      this.scanAndApply();
    }

    this.setupMutationObserver();
  }

  setupMutationObserver() {
    if ('MutationObserver' in window) {
      const mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                this.scanElement(node);
              }
            });
          }
        });
      });

      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  scanAndApply() {
    this.scanElement(document);
  }

  scanElement(container) {
    const elements = container.querySelectorAll?.(this.config.selector) || [];
    
    elements.forEach((element) => {
      if (!this.elements.has(element)) {
        this.elements.add(element);
        this.processElement(element);
      }
    });

    if (container.matches?.(this.config.selector) && !this.elements.has(container)) {
      this.elements.add(container);
      this.processElement(container);
    }
  }

  processElement(wrapper) {
    // Look for the main image (not in pixelated container)
    const mainImg = wrapper.querySelector('img:not(.pixelated-effect__img-container img)');
    const pixelatedImg = wrapper.querySelector('.pixelated-effect__img-container img');

    if (!mainImg) {
      return;
    }

    if (wrapper.dataset.pixelatedProcessed) {
      return;
    }
    wrapper.dataset.pixelatedProcessed = 'true';

    // Ensure wrapper starts without loaded class (showing pixelated image)
    wrapper.classList.remove(this.config.loadedClass);
    wrapper.classList.remove(this.config.errorClass);

    // Record when we started processing this element
    this.elementTimestamps.set(wrapper, Date.now());

    const loadHandler = () => this.handleImageLoad(wrapper, mainImg);
    const errorHandler = () => this.handleImageError(wrapper, mainImg);

    // Always wait for the load event, even if image appears loaded
    // This ensures we catch cases where the image might still be rendering
    mainImg.addEventListener('load', loadHandler, { once: true });
    mainImg.addEventListener('error', errorHandler, { once: true });

    // Check if main image is already loaded
    if (this.isImageLoaded(mainImg)) {
      // Image is already loaded, but still trigger the transition for consistency
      this.scheduleLoadedState(wrapper);
    } else {
      // If image has src but isn't complete, ensure it loads
      if (mainImg.src && !mainImg.complete) {
        // Don't force reload, just ensure load event will fire
        if (!mainImg.src.startsWith('data:') && !mainImg.src.startsWith('blob:')) {
          const originalSrc = mainImg.src;
          mainImg.src = '';
          requestAnimationFrame(() => {
            mainImg.src = originalSrc;
          });
        }
      }
    }

    // If pixelated image exists, ensure it's visible while main image loads
    if (pixelatedImg && !this.isImageLoaded(pixelatedImg)) {
      pixelatedImg.addEventListener('load', () => {
        // Pixelated image loaded, now wait for main image
        if (this.config.enableLogging) {
          console.log('Pixelated image loaded, waiting for main image');
        }
      }, { once: true });
    }
  }

  isImageLoaded(img) {
    return img.complete && img.naturalHeight !== 0 && img.naturalWidth !== 0;
  }

  handleImageLoad(wrapper, img) {
    // Double-check that the main image is actually fully loaded
    if (this.isImageLoaded(img)) {
      this.scheduleLoadedState(wrapper);
      if (this.config.enableLogging) {
        console.log('Main image fully loaded, transitioning from pixelated to main image');
      }
    } else {
      // If for some reason the image isn't fully loaded, wait a bit and check again
      setTimeout(() => {
        if (this.isImageLoaded(img)) {
          this.scheduleLoadedState(wrapper);
        }
      }, 100);
    }
  }

  handleImageError(wrapper, img) {
    wrapper.classList.add(this.config.errorClass);
    if (this.config.enableLogging) {
      console.error('Main image failed to load');
    }
  }

  scheduleLoadedState(wrapper) {
    const startTime = this.elementTimestamps.get(wrapper) || Date.now();
    const elapsedTime = Date.now() - startTime;
    const remainingMinTime = Math.max(0, this.config.minDisplayTime - elapsedTime);

    // Use requestAnimationFrame to ensure smooth transition
    requestAnimationFrame(() => {
      // Ensure minimum display time for pixelated image, then add transition delay
      const totalDelay = remainingMinTime + this.config.delay;

      setTimeout(() => {
        wrapper.classList.add(this.config.loadedClass);

        // Clean up timestamp tracking
        this.elementTimestamps.delete(wrapper);

        if (this.config.enableLogging) {
          console.log(`Applied loaded state after ${elapsedTime + totalDelay}ms, pixelated image should now fade out`);
        }
      }, totalDelay);
    });
  }

  refresh() {
    this.scanAndApply();
  }

  destroy() {
    this.elements.clear();
    this.elementTimestamps.clear();
  }

  static create(options = {}) {
    return new PixelatedEffect(options);
  }
}

const pixelatedEffect = PixelatedEffect.create({
  enableLogging: true
});

if (typeof module !== 'undefined' && module.exports) {
  module.exports = PixelatedEffect;
} else if (typeof window !== 'undefined') {
  window.PixelatedEffect = PixelatedEffect;
  window.pixelatedEffect = pixelatedEffect; 
}

// Usage examples:
/*
// Basic usage (auto-initialized above)
// The effect will automatically work on elements with class 'pixelated-effect__wrapper'

// Custom configuration
const customEffect = PixelatedEffect.create({
  selector: '.my-custom-wrapper',
  loadedClass: 'image-ready',
  errorClass: 'image-error',
  delay: 300,
  enableLogging: true
});

// Manual refresh after adding new content
pixelatedEffect.refresh();

// Clean up when needed
pixelatedEffect.destroy();
*/